// Account form component
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { accountSchema, type AccountInput } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { OwnerSelect } from './OwnerSelect'

interface Props {
  defaultValues?: Partial<AccountInput>
  onSubmit: (data: AccountInput) => void
  onCancel?: () => void
  loading?: boolean
  canWriteField?: (field: string) => boolean
}

export function AccountForm({ defaultValues, onSubmit, onCancel, loading, canWriteField = () => true }: Props) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AccountInput>({
    resolver: zodResolver(accountSchema),
    defaultValues,
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          {t('fields.accountName')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('name')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {canWriteField('type') && (
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
              {t('fields.type')}
            </label>
            <select
              {...register('type')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="">{t('common.select')}</option>
              <option value="prospect">{t('status.prospect')}</option>
              <option value="customer">{t('status.customer')}</option>
              <option value="partner">{t('status.partner')}</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
            )}
          </div>
        )}

        {canWriteField('industry') && (
          <div>
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
              {t('fields.industry')}
            </label>
            <input
              {...register('industry')}
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.industry && (
              <p className="mt-1 text-sm text-red-600">{errors.industry.message}</p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            {t('fields.phone')}
          </label>
          <input
            {...register('phone')}
            type="tel"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700">
            {t('fields.website')}
          </label>
          <input
            {...register('website')}
            type="url"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.website && (
            <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
          )}
        </div>
      </div>

      {/* Owner assignment - only visible to admins/managers */}
      <Controller
        name="ownerId"
        control={control}
        render={({ field }) => (
          <OwnerSelect
            value={field.value}
            onChange={field.onChange}
            disabled={loading}
          />
        )}
      />

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
